import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createUsuario,
  listRoles,
  type CreateUsuarioRequest,
} from "@/api/usuarios";
import { TextField } from "@/components/ui/TextField";
import { showToast } from "@/components/ui/Toast";
import { FormPageShell } from "@/components/ui/FormPageShell";

export const UsuarioNuevoPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: roles = [] } = useQuery({
    queryKey: ["roles"],
    queryFn: listRoles,
  });

  const create = useMutation({
    mutationFn: (body: CreateUsuarioRequest) => createUsuario(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["usuarios"] });
      showToast("success", "Usuario creado");
      navigate("/usuarios");
    },
    onError: (err: Error) =>
      showToast("error", "No se pudo crear", err.message),
  });

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const data: CreateUsuarioRequest = {
      nombre: String(form.get("nombre") ?? "").trim(),
      email: String(form.get("email") ?? "").trim(),
      password: String(form.get("password") ?? ""),
      rol_id: Number(form.get("rol_id") ?? 0),
    };
    create.mutate(data);
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <FormPageShell
        title="Crear usuario"
        submitLabel="Crear usuario"
        onCancel={() => navigate("/usuarios")}
        loading={create.isPending}
      >
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg bg-white p-6 shadow-card"
        >
          <TextField
            label="Nombre completo"
            name="nombre"
            required
            minLength={2}
          />
          <TextField
            label="Correo electrónico"
            name="email"
            type="email"
            required
          />
          <TextField
            label="Contraseña temporal"
            name="password"
            type="password"
            required
            minLength={8}
            hint="Mínimo 8 caracteres, una mayúscula, una minúscula, un número."
          />
          <TextField
            as="select"
            label="Rol del sistema"
            name="rol_id"
            required
            defaultValue=""
          >
            <option value="" disabled>
              Selecciona un rol
            </option>
            {roles.map((rol) => (
              <option key={rol.id} value={rol.id}>
                {rol.nombre}
              </option>
            ))}
          </TextField>
        </form>
      </FormPageShell>
    </div>
  );
};
